import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@auth0/auth0-angular';
import { HelpComponent } from './components/pages/help/help.component';
import { ContactCinchComponent } from './components/pages/contact-cinch/contact-cinch.component';
import { DisclosureComponent } from './components/pages/disclosure/disclosure.component';
import { TermsOfUseComponent } from './components/pages/terms-of-use/terms-of-use.component';
import { PrivacyPolicyComponent } from './components/pages/privacy-policy/privacy-policy.component';
import { NotFoundComponent } from './components/pages/not-found/not-found.component';
import { ApiErrorComponent } from './components/pages/api-error/api-error.component';
import { SessionTimeoutComponent } from './components/pages/session-timeout/session-timeout.component';
import { FlagPreloadingStrategy } from './utils/flag-preloading-strategy';
import { vivrGuard } from './guards/vivr.guard';
import { SpecialMemberOfferComponent } from './components/pages/special-member-offer/special-member-offer.component';
import { SpecialMemberOfferV2Component } from './components/pages/special-member-offer-v2/special-member-offer-v2.component';
import { EnrollReviewComponent } from './components/pages/special-member-offer-v2/enroll-review/enroll-review.component';
import { EnrollSuccessComponent } from './components/pages/special-member-offer-v2/enroll-success/enroll-success.component';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./components/pages/dashboard/dashboard.module').then(
        (m) => m.DashboardModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'login',
    loadChildren: () =>
      import('./components/pages/login/login.module').then(
        (m) => m.LoginModule
      ),
  },
  {
    path: 'registration',
    loadChildren: () =>
      import('./components/pages/registration/registration.module').then(
        (m) => m.RegistrationModule
      ),
  },
  {
    path: 'password-reset',
    loadChildren: () =>
      import('./components/pages/password-reset/password-reset.module').then(
        (m) => m.PasswordResetModule
      ),
  },
  {
    path: 'shared',
    loadChildren: () =>
      import('./components/pages/shared/shared.components.module').then(
        (m) => m.SharedComponentsModule
      ),
  },
  {
    path: 'service-request',
    loadChildren: () =>
      import('./components/pages/service-request/service-request.module').then(
        (m) => m.ServiceRequestModule
      ),
    data: { preload: true }, // Preload this module
    canActivate: [vivrGuard],
  },
  {
    path: 'profile',
    loadChildren: () =>
      import('./components/pages/profile/profile.module').then(
        (m) => m.ProfileModule
      ),
      canActivate: [AuthGuard],
  },
  {
    path: 'perks-and-benefits',
    loadChildren: () =>
      import('./components/pages/perks-benefits/perks-benefits.module').then(
        (m) => m.PerksBenefitsModule
      ),
  },
  {
    path: 'help',
    component: HelpComponent,
  },
  {
    path: 'contact-us',
    component: ContactCinchComponent,
  },
  {
    path: 'my-plan',
    loadChildren: () =>
      import('./components/pages/my-plan/my-plan.module').then(
        (m) => m.MyPlanModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'payments',
    loadChildren: () =>
      import('./components/pages/payments/payments.module').then(
        (m) => m.PaymentsModule
    ),
    canActivate: [AuthGuard],
  },
  {
    path: 'cancel-plan',
    loadChildren: () =>
      import('./components/pages/cancel-plan/cancel-plan.module').then(
      (m) => m.CancelPlanModule
    ),
    canActivate: [AuthGuard],
  },
  {
    path: 'disclosure',
    component: DisclosureComponent,
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyComponent,
  },
  {
    path: 'terms-of-use',
    component: TermsOfUseComponent,
  },
  {
    path: 'special-member-offer',
    component: SpecialMemberOfferComponent,
    canActivate: [vivrGuard]
  },
  {
    path: 'special-member-offer-form',
    component: SpecialMemberOfferComponent,
    canActivate: [vivrGuard]
  },
  {
    path: 'special-member-offer-v2',
    component: SpecialMemberOfferV2Component,
    canActivate: [vivrGuard]
  },
  {
    path: 'special-member-offer-v2/enroll-review',
    component: EnrollReviewComponent,
    canActivate: [vivrGuard]
  },
  {
    path: 'special-member-offer-v2/enroll-success',
    component: EnrollSuccessComponent,
    canActivate: [vivrGuard]
  },
  {
    path: 'special-member-offer-form-v2',
    component: SpecialMemberOfferV2Component,
    canActivate: [vivrGuard]
  },
  {
    path: 'make-payment',
    loadChildren: () =>
      import('./components/pages/make-payment/make-payment.module').then(
        (m) => m.MakePaymentModule
      ),
      canActivate: [AuthGuard],
  },
  // VIVR
  {
    path: 'make-payment-vivr',
    loadChildren: () =>
      import('./components/pages/make-payment-vivr/make-payment-vivr.module').then(
        (m) => m.MakePaymentVivrModule
      ),
    canActivate: [vivrGuard],
  },
  {
    path: 'claim-history',
    loadChildren: () => import('./components/pages/claim-history/claim-history.module').then( (m) => m.ClaimHistoryModule),
     canActivate: [vivrGuard],
  },
  {
    path: 'my-service-requests',
    loadChildren: () =>
      import('./components/pages/service-request-history/service-request-history.module').then(
        (m) => m.ServiceRequestHistoryModule
      ),
    canActivate: [AuthGuard],
  },
  { path: 'session-timeout',component: SessionTimeoutComponent },
  { path: '404', component: NotFoundComponent},
  { path: 'oops_page', component: ApiErrorComponent},
  { path: '**', redirectTo: '404' }

];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: FlagPreloadingStrategy,
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
